/**
 * Endereços das telas (ficam depois do # na barra do navegador):
 *   #/painel/2026-10            painel do mês (Aliança: marcas do Winthor)
 *   #/painel/2026-10/bauny      painel do mês da Bauny (SAP)
 *   #/representante/5/2026-10   um representante no mês (/bauny no fim = Bauny)
 *   #/importar                  importar arquivo
 *   #/importacoes               histórico de importações
 *   #/metas/2026-10             metas do mês
 *   #/cadastros/2026-10         representantes (fotos, saiu da equipe), códigos, marcas, feriados e logo
 *   #/relatos                   problemas e sugestões enviados pelo botão "Relatar"
 *   #/conta                     trocar a minha senha
 *   #/sistema                   usuários, saúde do servidor, cópias do banco e alterações (só admin)
 * Usar o # evita configurar o servidor: qualquer endereço abre o index.html.
 *
 * Lê: window.location.hash.
 * Usado por: App.tsx e pelos links das telas.
 */
import { useEffect, useState } from 'react';
import { ehMes, mesAtual } from './dominio/datas.ts';
import type { Fonte, Mes } from './dominio/tipos.ts';

/** fonte vazia = Winthor (Aliança). */
export type Rota =
  | { tela: 'painel'; mes: Mes; fonte?: Fonte }
  | { tela: 'representante'; id: number; mes: Mes; fonte?: Fonte }
  | { tela: 'importar' }
  | { tela: 'importacoes' }
  | { tela: 'relatos' }
  | { tela: 'conta' }
  | { tela: 'sistema' }
  | { tela: 'metas'; mes: Mes }
  | { tela: 'cadastros'; mes: Mes };

/** No endereço, a Bauny é "/bauny" no fim; sem nada = Winthor. */
const fonteDoEndereco = (texto: string): Fonte => (texto === 'bauny' ? 'sap-bauny' : 'winthor');
const sufixoDaFonte = (fonte: Fonte | undefined): string => (fonte === 'sap-bauny' ? '/bauny' : '');

export function lerRota(hash: string): Rota {
  const [, tela = '', a = '', b = '', c = ''] = hash.replace(/^#/, '').split('/');
  const mesOu = (texto: string): Mes => (ehMes(texto) ? texto : mesAtual());
  switch (tela) {
    case 'representante':
      return Number.isInteger(Number(a)) && a !== '' ? { tela, id: Number(a), mes: mesOu(b), fonte: fonteDoEndereco(c) } : { tela: 'painel', mes: mesAtual() };
    case 'importar':
    case 'importacoes':
    case 'relatos':
    case 'conta':
    case 'sistema':
      return { tela };
    case 'metas':
    case 'cadastros':
      return { tela, mes: mesOu(a) };
    default:
      return { tela: 'painel', mes: mesOu(a), fonte: fonteDoEndereco(b) };
  }
}

export function linkPara(rota: Rota): string {
  switch (rota.tela) {
    case 'representante':
      return `#/representante/${rota.id}/${rota.mes}${sufixoDaFonte(rota.fonte)}`;
    case 'painel':
      return `#/painel/${rota.mes}${sufixoDaFonte(rota.fonte)}`;
    case 'importar':
    case 'importacoes':
    case 'relatos':
    case 'conta':
    case 'sistema':
      return `#/${rota.tela}`;
    default:
      return `#/${rota.tela}/${rota.mes}`;
  }
}

export function irPara(rota: Rota): void {
  window.location.hash = linkPara(rota);
}

/** A rota atual, atualizada quando o endereço muda. */
export function usarRota(): Rota {
  const [rota, setRota] = useState(() => lerRota(window.location.hash));
  useEffect(() => {
    const aoMudar = () => {
      setRota(lerRota(window.location.hash));
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', aoMudar);
    return () => window.removeEventListener('hashchange', aoMudar);
  }, []);
  return rota;
}
